import classNames from "classnames"
import AvailabilityCalendar from "legacy/availability/AvailabilityCalendar"
import AvailabilityDonut from "legacy/availability/AvailabilityDonut"
import { inchesToFeet, inchesToSquareFeet } from "legacy/helpers.es6"
import _ from "lodash"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"

import Badge from "src/components/Badge"

// Note: jQuery ($) is loaded globally, not via imports

class Availability extends React.Component {
  constructor() {
    super()
    this.state = {
      expandedRows: [],
    }
    this.toggleDetails = this.toggleDetails.bind(this)
  }

  toggleDetails(rowId) {
    $(".popover").remove()
    const currentExpandedRows = this.state.expandedRows
    const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId)
    const newExpandedRows = isRowCurrentlyExpanded
      ? currentExpandedRows.filter((id) => id !== rowId)
      : currentExpandedRows.concat(rowId)
    this.setState({ expandedRows: newExpandedRows })
  }

  splitToMonths(nightsData) {
    return _.groupBy(this.arrayOfNightsData(nightsData), "month")
  }

  arrayOfNightsData(nightsData) {
    return _.values(
      _.map(nightsData, (value, key) => {
        value.night = key
        return value
      })
    )
  }

  sortDataByDisplayOrder() {
    const array = _.map(this.props.data.occupancies, (value, key) => {
      value.id = key
      return value
    })
    return _.orderBy(array, ["display_order"], ["asc"])
  }

  roundToViewablePercentage(percent) {
    if (percent > 0 && percent < 1) {
      return 1
    } else {
      return percent.toFixed(0)
    }
  }

  occupancy(spaceGroupData, night) {
    if (
      ["SpaceGroup::Linear", "SpaceGroup::LinearV2"].includes(
        spaceGroupData.type
      )
    ) {
      return night.loa
    } else if (spaceGroupData.type === "SpaceGroup::SquareFootage") {
      return night.square_inches
    } else {
      return night.count
    }
  }

  warningPercent(spaceGroupData) {
    const total = spaceGroupData.inventory_total
    let numOfWarningNights = 0
    _.each(spaceGroupData.nights, (night) => {
      const occupancy = this.occupancy(spaceGroupData, night)
      if (
        (occupancy < total && occupancy >= total * 0.9) ||
        total - occupancy === 1
      ) {
        numOfWarningNights += 1
      }
    })
    const percent = (numOfWarningNights / _.size(spaceGroupData.nights)) * 100
    return this.roundToViewablePercentage(percent)
  }

  dangerPercent(spaceGroupData) {
    const total = spaceGroupData.inventory_total
    let numOfDangerNights = 0
    _.each(spaceGroupData.nights, (night) => {
      const occupancy = this.occupancy(spaceGroupData, night)
      if (occupancy >= total) {
        numOfDangerNights += 1
      }
    })
    const percent = (numOfDangerNights / _.size(spaceGroupData.nights)) * 100
    return this.roundToViewablePercentage(percent)
  }

  titleClass() {
    return "text-muted font-semibold font-size-10 bs-mb-2"
  }

  rowClass() {
    return "flex items-center"
  }

  headerSpacing() {
    if (this.isShortTimeFrame()) {
      return "minmax(50px, 70px) 1fr 4fr"
    } else if (this.props.compact) {
      return "minmax(50px, 70px) 1fr 1fr 1fr 1fr"
    } else {
      return "minmax(50px, 70px) 2fr 1fr 1fr 1fr 1fr 1fr"
    }
  }

  renderExpandButton(isExpanded) {
    return (
      <button type="button" className="btn btn-ghost group-hover:bg-blue-50">
        <span className="text-transparent group-hover:text-blue-700">{`${
          isExpanded ? "Hide" : "Show"
        } Calendar`}</span>
        <i
          className={classNames(
            "fa-solid fa-chevron-right transition duration-100",
            {
              "fa-rotate-90": isExpanded,
            }
          )}
          style={{ fontSize: 20 }}
        />
      </button>
    )
  }

  renderExpandIcon(isExpanded) {
    return (
      <i
        className={classNames(
          "fa-solid fa-chevron-right transition duration-100",
          {
            "fa-rotate-90": isExpanded,
          }
        )}
        style={{ fontSize: 20 }}
      />
    )
  }

  renderTitledItem(title, value) {
    return (
      <div className={`px-1 ${this.rowClass()}`}>
        <span className="inline-block">
          <div className={this.titleClass()}>{title.toUpperCase()}</div>
          {value}
        </span>
      </div>
    )
  }

  inchesToFeetString(value) {
    return `${inchesToFeet(value)}'`
  }

  squareInchesToSquareFeetString(value) {
    return `${inchesToSquareFeet(value)}sqft`
  }

  dimensionDisplayRange(min, max) {
    if (max === 1200) {
      return `${this.inchesToFeetString(min)}+`
    } else {
      return `${this.inchesToFeetString(min)} - ${this.inchesToFeetString(max)}`
    }
  }

  shouldDisplayDimension(min, max) {
    return min !== 0 || max !== 12000
  }

  isShortTimeFrame() {
    const nights = Object.keys(
      Object.values(this.props.data.occupancies)[0].nights
    )
    const start = moment(nights[0])
    const end = moment(nights[nights.length - 1])
    return end.diff(start, "days") <= 14
  }

  popoverData(data, totalString) {
    return `
      <div class="space-y-5">
        <div class="row form-row">
          <div class="col-xs-6">
            <div class="${this.titleClass()}">TOTAL</div>
            ${totalString}
          </div>
          <div class="col-xs-6">
            <div class="${this.titleClass()}">LOA</div>
            ${this.dimensionDisplayRange(data.min_length, data.max_length)}
          </div>
        </div>
        <div class="row form-row">
          <div class="col-xs-6">
            <div class="${this.titleClass()}">DRAFT</div>
            ${this.dimensionDisplayRange(data.min_draft, data.max_draft)}
          </div>
          <div class="col-xs-6">
            <div class="${this.titleClass()}">BEAM</div>
            ${this.dimensionDisplayRange(data.min_beam, data.max_beam)}
          </div>
        </div>
      </div>
    `
  }

  renderRow(data) {
    const spaceGroupID = data.id
    const onRowClick = this.isShortTimeFrame()
      ? () => {}
      : () => this.toggleDetails(spaceGroupID)
    const isExpanded = this.state.expandedRows.includes(spaceGroupID)
    const isSuggested = this.props.suggestedGroupID === spaceGroupID
    let totalString = data.inventory_total
    let kindString = data.dock_type || "All"
    if (["SpaceGroup::Linear", "SpaceGroup::LinearV2"].includes(data.type)) {
      totalString = this.inchesToFeetString(data.inventory_total)
      kindString = "Linear dock"
    } else if (data.type === "SpaceGroup::SquareFootage") {
      totalString = this.squareInchesToSquareFeetString(data.inventory_total)
    }
    const itemRows = []

    if (this.isShortTimeFrame()) {
      itemRows.push(
        <AvailabilityCalendar
          key={spaceGroupID}
          nights={this.arrayOfNightsData(data.nights)}
          type={data.type}
          total={data.inventory_total}
          isShortTimeFrame={true}
        />
      )
    } else if (this.props.compact) {
      itemRows.push(
        this.renderTitledItem(
          "loa",
          this.dimensionDisplayRange(data.min_length, data.max_length)
        ),
        <div key="empty-cell-1" />,
        <div key="expand-button-1" className={this.rowClass()}>
          {this.renderExpandButton(isExpanded)}
        </div>
      )
    } else {
      itemRows.push(
        this.renderTitledItem(
          "loa",
          this.dimensionDisplayRange(data.min_length, data.max_length)
        ),
        this.shouldDisplayDimension(data.min_draft, data.max_draft) ? (
          this.renderTitledItem(
            "draft",
            this.dimensionDisplayRange(data.min_draft, data.max_draft)
          )
        ) : (
          <div key="empty-cell-2" />
        ),
        this.shouldDisplayDimension(data.min_beam, data.max_beam) ? (
          this.renderTitledItem(
            "beam",
            this.dimensionDisplayRange(data.min_beam, data.max_beam)
          )
        ) : (
          <div key="empty-cell-3" />
        ),
        <div key="empty-cell-4" />,
        <div key="expand-button-2" className={this.rowClass()}>
          {this.renderExpandButton(isExpanded)}
        </div>
      )
    }

    if (isExpanded) {
      itemRows.push(
        <div key="expanded-empty-cell" className={this.rowClass()} />,
        <div
          key="expanded-content"
          className="bs-py-2 border-t"
          style={{ gridColumn: "2 / -1" }}
        >
          {_.map(this.splitToMonths(data.nights), (nights) => {
            return (
              <AvailabilityCalendar
                key={nights.month}
                nights={nights}
                kind={data.kind}
                total={data.inventory_total}
                isShortTimeFrame={false}
              />
            )
          })}
        </div>
      )
    }
    return (
      <div key={spaceGroupID}>
        <div
          className={`${isSuggested ? "border-soft-green rounded border" : ""}`}
        >
          {isSuggested && (
            <div
              className="mx-auto w-fit"
              style={{ marginTop: -10, marginBottom: -8 }}
            >
              <Badge color="positive" text="Recommended" />
            </div>
          )}
          <div
            className="group grid cursor-pointer"
            onClick={onRowClick}
            style={{ gridTemplateColumns: this.headerSpacing() }}
            data-content={this.popoverData(data, totalString)}
            data-container="body"
            data-toggle="popover"
            data-placement="auto left"
            data-animation="false"
            data-html="true"
          >
            <div className={`bs-py-4 px-2 ${this.rowClass()}`}>
              <AvailabilityDonut
                warningPercent={this.warningPercent(data)}
                dangerPercent={this.dangerPercent(data)}
              />
            </div>
            {this.renderTitledItem(kindString, data.name)}
            {itemRows}
          </div>
        </div>
      </div>
    )
  }

  issueRow(data, rowID, title, link, expandedHtml) {
    const count = data.length
    const totalLoa = _.sumBy(data, (overage) => overage.loa)
    const onRowClick = () => this.toggleDetails(rowID)
    const isExpanded = this.state.expandedRows.includes(rowID)

    return (
      <div key={rowID}>
        <div
          className="grid cursor-pointer"
          onClick={onRowClick}
          style={{
            gridTemplateColumns:
              "minmax(50px, 70px) 2fr 1fr 1fr 1fr 1fr minmax(20px, 40px)",
            minHeight: "70px",
          }}
        >
          <div className={`bs-py-4 relative px-2 ${this.rowClass()}`}>
            <svg
              width="40px"
              height="40px"
              viewBox="0 0 42 42"
              className="donut"
            >
              <circle
                className="donut-hole"
                cx="50%"
                cy="50%"
                r="15.91549430918954"
                fill="#fff"
              />
              <circle
                className="donut-segment"
                cx="50%"
                cy="50%"
                r="15.91549430918954"
                fill="transparent"
                style={{
                  stroke: "#c8cfd4",
                  strokeWidth: 7,
                }}
              />
              <div
                className="font-semibold"
                style={{
                  position: "absolute",
                  left: 23,
                  lineHeight: 12,
                  fontSize: 20,
                  color: "#c8cfd4",
                }}
              >
                !
              </div>
            </svg>
          </div>
          <div className={this.rowClass()}>
            <span className="inline-block">
              <div className={this.titleClass()}>POTENTIAL ISSUE</div>
              <span>{title}</span>
            </span>
          </div>
          {this.renderTitledItem("Total boats", count)}
          {this.renderTitledItem(
            "Total loa",
            this.inchesToFeetString(totalLoa)
          )}
          <div className={this.rowClass()} style={{ gridColumn: "5 / 7" }}>
            {link}
          </div>
          <div className={this.rowClass()} style={{ gridColumn: "7 / -1" }}>
            <div className={this.rowClass()}>
              {this.renderExpandIcon(isExpanded)}
            </div>
          </div>
          {isExpanded && expandedHtml}
        </div>
      </div>
    )
  }

  overflowExpandedText() {
    return [
      <div key="overflow-section-1" className={this.rowClass()} />,
      <div
        key="overflow-section-2"
        className="bs-py-4 border-t"
        style={{ gridColumn: "2 / -1" }}
      >
        <div className="font-semibold">What does this mean?</div>
        <div>
          The requested space type and dimensions of these boats do not fit in
          any currently configured space groups.
        </div>
        <div className="bs-mt-3 font-semibold">What can you do?</div>
        <div>
          The availability calculations will be more accurate if you assign
          boats you wish to accommodate or adjust your space group settings.
        </div>
      </div>,
    ]
  }

  overflowLink() {
    const ids = _.map(
      this.props.data.overages,
      (overage) => overage.reservation_encoded_id
    ).join("+")
    return (
      <a
        className="inline-block font-semibold"
        target="_blank"
        rel="noopener noreferrer"
        href={`${$context.managePath}/reservations?search=${ids}`}
      >
        View and assign reservations →
      </a>
    )
  }

  spaceGrouplessExpandedText() {
    return [
      <div key="space-group-less-section-1" className={this.rowClass()} />,
      <div
        key="space-group-less-section-2"
        className="bs-py-4 border-t"
        style={{ gridColumn: "2 / -1" }}
      >
        <div className="font-semibold">What does this mean?</div>
        <div>
          There are some reservations that are assigned to spaces that do not
          have space groups. This becomes an issue when trying to view
          availability by space group.
        </div>
        <div className="bs-mt-3 font-semibold">What can you do?</div>
        <div>
          The availability calculations will be more accurate if you adjust your
          space settings so they are associated to a space group.
        </div>
      </div>,
    ]
  }

  spaceGrouplessLink() {
    return (
      <a
        className="inline-block font-semibold"
        target="_blank"
        rel="noopener noreferrer"
        href={`${$context.managePath}/settings#spaces`}
      >
        Edit space settings →
      </a>
    )
  }

  renderSpaceGroups() {
    if (!this.props.data) {
      return null
    }
    if (this.props.data.has_space_groups) {
      return (
        <div
          className="bs-p-2 divide-y transition duration-100"
          style={{ opacity: this.props.loading ? 0.25 : 1 }}
        >
          {_.map(this.sortDataByDisplayOrder(), (occupancies) =>
            this.renderRow(occupancies)
          )}
          {!this.props.compact &&
            this.props.data.overages &&
            this.props.data.overages.length > 0 &&
            this.issueRow(
              this.props.data.overages,
              "overage",
              "Boats that don't fit",
              this.overflowLink(),
              this.overflowExpandedText()
            )}
          {!this.props.compact &&
            this.props.data.space_groupless &&
            this.props.data.space_groupless.length > 0 &&
            this.issueRow(
              this.props.data.space_groupless,
              "spaceGroupless",
              "Spaces with no Space Group",
              this.spaceGrouplessLink(),
              this.spaceGrouplessExpandedText()
            )}
        </div>
      )
    } else {
      return (
        <div className="bs-p-2">
          <span className="bs-mr-2">
            {" "}
            Please set up space groups to gain more insight into your
            marina&apos;s availability.
          </span>
          <a
            className="inline-block font-semibold"
            target="_blank"
            rel="noopener noreferrer"
            href={`${$context.managePath}/settings/space_groups`}
          >
            Set up space groups →
          </a>
        </div>
      )
    }
  }

  renderLoadingView() {
    return (
      <div className="flex justify-center">
        <div>
          <div className="text-muted text-center">Loading Availability...</div>
          <div className="bs-mt-4 text-center">
            <i
              style={{ fontSize: "2rem" }}
              className="fa-solid fa-spinner-third fa-spin text-muted"
            />
          </div>
        </div>
      </div>
    )
  }

  render() {
    return (
      <div className="row form-row bs-pt-1">
        {this.props.loading && !this.props.data && this.renderLoadingView()}
        {this.renderSpaceGroups()}
      </div>
    )
  }
}

Availability.propTypes = {
  loading: PropTypes.bool,
  data: PropTypes.object,
  suggestedGroupID: PropTypes.string,
  compact: PropTypes.bool,
}

export default Availability
